import { addDays, DateOnly } from '@if/core/date'
import { formatCurrency } from '@if/core/utils'
import { ErrorAlert, useDialog, useIfUser } from '@if/mui'
import { Stack } from '@mui/material'
import { useTranslatedPaymentMethods } from '@src/types/constants'
import { SignedCalendarSimulation } from '@src/types/loan/loan-simulation'
import { CalendarCog } from 'lucide-react'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useGetOutstandingBalance } from '../LoanCalendar-hooks'
import { CanSpreadOverdueAmountOnDates } from '../LoanSpreadOverdueAmountDialog/LoanSpreadOverdueAmount-selector'
import { useLoanSpreadOverdueAmountDialog } from '../LoanSpreadOverdueAmountDialog/LoanSpreadOverdueAmountDialog-hook'
import { useCatchUpAmountOfOverdueForDateDialog } from './CatchUpAmountOfOverdueForDate/CatchUpAmountOfOverdueForDateDialog-hook'
import { EEditPaymentChangeType, useEditPaymentsAction } from './EditPayments-action'
import { useActivePaymentConfig } from './EditPayments-hooks'
import { EEditPaymentOperationKind } from './EditPayments-operations'
import { isReturnToCalendar } from './EditPayments-tool'
import EditPaymentsDialogBody from './EditPaymentsDialogBody'
import EditPaymentsImpact from './EditPaymentsImpact'
import EditPaymentsReviewChanges from './EditPaymentsReviewChanges'
import EditPaymentsToolbar from './EditPaymentsToolbar'
import { useLoanAddPaymentDialog } from './LoanAddPayment/LoanAddPaymentDialog-hook'
import { useLoanAddRecurringPaymentDialog } from './LoanAddRecurringPayment/LoanAddRecurringPaymentDialog-hook'
import {
  BatchUpdatePaymentsProps,
  useLoanBatchUpdatePaymentsDialog,
} from './LoanBatchUpdatePayments/LoanBatchUpdatePaymentsDialog-hook'
import { useMoveSelectedPaymentsDialog } from './MoveSelectedPaymentDialog-hook'
import { useNoteDialog } from './NoteDialog-hook'

export enum EEditPaymentsSubAction {
  AddRecurringPayments = 'AddRecurringPayments',
  AddPayment = 'AddPayment',
  DeletePayment = 'DeletePayment',
  SpreadLateAmount = 'SpreadLateAmount',
  CatchUpAmountOfOverdueForDate = 'CatchUpAmountOfOverdueForDate',
  AddNoteToAllPayments = 'AddNoteToAllPayments',
  MovePayments = 'MovePayments',
  BatchUpdatePayments = 'BatchUpdatePayments',
  IncreasePayments = 'IncreasePayments',
}

export interface EditPaymentsDialogProps {
  initialSignedSimulation: SignedCalendarSimulation
  selectedPaymentIds: Set<string>
  defaultSubAction?: EEditPaymentsSubAction
  isCatchUpMode?: boolean
  isIncreasingPayments?: boolean
  isLockedByOther?: boolean
}

const useEditPaymentsDialogState = (props: EditPaymentsDialogProps) => {
  const {
    initialSignedSimulation,
    selectedPaymentIds,
    defaultSubAction,
    isCatchUpMode = false,
    isIncreasingPayments = true,
    isLockedByOther = false,
  } = props

  const user = useIfUser()
  const canBypassAcpLock = user.rights.canEditLockedPpaPayments
  const { loan } = initialSignedSimulation.simulation.fullLoan
  const activationOn = loan.activationOn
  const loanEndDate = loan.lastPaymentOn

  const {
    addPayment,
    deletePayment,
    deleteAllPayments,
    restorePayment,
    onCriticalChange,
    onUncriticalChange,
    payments,
    error,
    isPending,
    isRecomputing,
    isValid,
    getConfirmedSimulation,
    currentSimulation,
    setBatchValues,
    formMethods,
    resetWithPayments,
    hasEdits,
    baseline,
    operations,
    runOperation,
    undoToOperation,
  } = useEditPaymentsAction({
    id: loan.id,
    financingCompanyId: loan.financingCompanyId,
    initialSignedSimulation,
    defaultSubAction,
    selectedPaymentIds,
  })

  const {
    formState: { isLoading: isLoadingDefaultValues },
  } = formMethods

  const paymentMethodOptions = useTranslatedPaymentMethods()
  const activePaymentConfig = useActivePaymentConfig(initialSignedSimulation.simulation.fullLoan.paymentConfigs)
  const defaultPaymentAmount = activePaymentConfig?.amount ?? 0

  const paymentDates = useMemo(() => payments.map((p) => p.effectiveDate), [payments])
  const batchUpdatePayments = useMemo(() => payments.map((p) => ({ ...p, note: p.note ?? '' })), [payments])

  // Every tool the toolbar can run, declared once. The library stacks each one over the
  // editor itself, so none of them needs to know the editor is there.
  const addPaymentDialog = useLoanAddPaymentDialog({
    loanActivationDate: activationOn,
    paymentMethodOptions,
    initialAmount: defaultPaymentAmount,
  })
  const addRecurringPaymentDialog = useLoanAddRecurringPaymentDialog({
    loanActivationDate: activationOn,
    loanEndDate,
    paymentMethodOptions,
  })
  const catchUpForDateDialog = useCatchUpAmountOfOverdueForDateDialog({ simulation: currentSimulation })
  const batchUpdateDialog = useLoanBatchUpdatePaymentsDialog({
    simulation: currentSimulation,
    payments: batchUpdatePayments,
  })
  const noteDialog = useNoteDialog()
  const moveSelectedPaymentsDialog = useMoveSelectedPaymentsDialog()
  const spreadOverdueAmountDialog = useLoanSpreadOverdueAmountDialog(currentSimulation)

  const stagePayments = useCallback(
    (kind: EEditPaymentOperationKind) => (added: Parameters<typeof addPayment>[0]) => {
      runOperation(kind, undefined, () => addPayment(added))
    },
    [addPayment, runOperation],
  )

  /**
   * Opens one tool over the editor and applies whatever it answers. A tool that answers
   * "return to calendar" closes the editor under it — the only reason `closeEditor` is
   * threaded in, and why it comes from the slot that has it rather than being held in state.
   */
  const runTool = useCallback(
    async (tool: EEditPaymentsSubAction, closeEditor: () => void, batchUpdate?: BatchUpdatePaymentsProps) => {
      switch (tool) {
        case EEditPaymentsSubAction.AddPayment: {
          const result = await addPaymentDialog.create()
          if (isReturnToCalendar(result)) return closeEditor()
          if (result) stagePayments(EEditPaymentOperationKind.AddPayment)(result)
          return
        }

        case EEditPaymentsSubAction.AddRecurringPayments: {
          const result = await addRecurringPaymentDialog.create()
          if (isReturnToCalendar(result)) return closeEditor()
          if (result) stagePayments(EEditPaymentOperationKind.AddRecurringPayments)(result)
          return
        }

        case EEditPaymentsSubAction.CatchUpAmountOfOverdueForDate: {
          const result = await catchUpForDateDialog.create()
          if (isReturnToCalendar(result)) return closeEditor()
          if (result) stagePayments(EEditPaymentOperationKind.CatchUpAmountOfOverdueForDate)(result)
          return
        }

        case EEditPaymentsSubAction.BatchUpdatePayments: {
          const result = await batchUpdateDialog.create({
            isCatchUpMode: batchUpdate?.isCatchUpMode ?? false,
            isIncreasingPayments: batchUpdate?.isIncreasingPayments ?? true,
          })
          if (isReturnToCalendar(result)) return closeEditor()
          if (!result) return

          if (result.kind === 'catchUp') {
            const { fromDate, numberOfPayments, amount, lastPaymentAmount } = result
            runOperation(
              EEditPaymentOperationKind.CatchUpOnRemainingPayments,
              { amount: formatCurrency(amount) },
              () => {
                resetWithPayments(fromDate, numberOfPayments)
                setBatchValues(
                  'amount',
                  (defaultAmount, isLast) => defaultAmount + (isLast ? lastPaymentAmount : amount),
                  EEditPaymentChangeType.Critical,
                )
                setBatchValues('sendSmsReminder', true, EEditPaymentChangeType.Critical, fromDate)
              },
            )
            return
          }

          const { amount, fromPaymentDate } = result
          const kind =
            amount >= 0 ? EEditPaymentOperationKind.IncreasePayments : EEditPaymentOperationKind.ReducePayments
          runOperation(kind, { amount: formatCurrency(Math.abs(amount)) }, () =>
            setBatchValues(
              'amount',
              (oldAmount) => oldAmount + amount,
              EEditPaymentChangeType.Critical,
              fromPaymentDate,
            ),
          )
          return
        }

        case EEditPaymentsSubAction.AddNoteToAllPayments: {
          const result = await noteDialog.create()
          if (isReturnToCalendar(result)) return closeEditor()
          if (!result) return
          runOperation(EEditPaymentOperationKind.AddNoteToAllPayments, undefined, () =>
            setBatchValues('note', result.note ?? '', EEditPaymentChangeType.Uncritical),
          )
          return
        }

        case EEditPaymentsSubAction.MovePayments: {
          const result = await moveSelectedPaymentsDialog.create()
          if (isReturnToCalendar(result)) return closeEditor()
          if (!result) return
          runOperation(EEditPaymentOperationKind.MovePayments, { days: result.nbOfDays }, () =>
            setBatchValues(
              'effectiveDate',
              (oldDate) => addDays(oldDate, result.nbOfDays),
              EEditPaymentChangeType.Critical,
            ),
          )
          return
        }

        case EEditPaymentsSubAction.SpreadLateAmount: {
          const amountToRaise = await spreadOverdueAmountDialog.open(paymentDates)
          if (isReturnToCalendar(amountToRaise)) return closeEditor()
          if (amountToRaise == null) return
          runOperation(EEditPaymentOperationKind.SpreadLateAmount, { amount: formatCurrency(amountToRaise) }, () =>
            setBatchValues('amount', (oldAmount) => oldAmount + amountToRaise, EEditPaymentChangeType.Critical),
          )
          return
        }

        // DeletePayment stages its deletions through the form's defaults, so it opens nothing.
        default:
          return
      }
    },
    [
      addPaymentDialog,
      addRecurringPaymentDialog,
      batchUpdateDialog,
      catchUpForDateDialog,
      moveSelectedPaymentsDialog,
      noteDialog,
      paymentDates,
      resetWithPayments,
      runOperation,
      setBatchValues,
      spreadOverdueAmountDialog,
      stagePayments,
    ],
  )

  const handleSmsReminderBatchChange = useCallback(
    (checked: boolean) => {
      const kind = checked
        ? EEditPaymentOperationKind.EnableSmsReminders
        : EEditPaymentOperationKind.DisableSmsReminders
      runOperation(kind, undefined, () => setBatchValues('sendSmsReminder', checked, EEditPaymentChangeType.Uncritical))
    },
    [runOperation, setBatchValues],
  )

  const hasAmountNotReplaced = useGetOutstandingBalance(initialSignedSimulation.simulation) > 0

  const canSpreadOverPayment = useMemo(
    () => CanSpreadOverdueAmountOnDates(currentSimulation.simulation, paymentDates, canBypassAcpLock),
    [currentSimulation, paymentDates, canBypassAcpLock],
  )

  const scope = useMemo(() => {
    const times = paymentDates.filter(Boolean).map((date) => date.getTime())
    return {
      count: payments.length,
      from: times.length > 0 ? new DateOnly(Math.min(...times)) : null,
      to: times.length > 0 ? new DateOnly(Math.max(...times)) : null,
      contractNumber: loan.contractNumber,
    }
  }, [loan.contractNumber, paymentDates, payments.length])

  // What an opener asked to run on top of the editor, and in which of the batch tool's modes.
  const defaultBatchUpdate = useMemo<BatchUpdatePaymentsProps>(
    () => ({ isCatchUpMode, isIncreasingPayments }),
    [isCatchUpMode, isIncreasingPayments],
  )

  return {
    activationOn,
    scope,
    selectedPaymentCount: selectedPaymentIds.size,
    baseline,
    canSpreadOverPayment,
    currentSimulation,
    defaultBatchUpdate,
    defaultTool: defaultSubAction,
    deleteAllPayments,
    deletePayment,
    error,
    formMethods,
    getConfirmedSimulation,
    handleSmsReminderBatchChange,
    hasAmountNotReplaced,
    hasEdits,
    initialSignedSimulation,
    isLoadingDefaultValues,
    isLockedByOther,
    isPending,
    isRecomputing,
    isValid,
    onCriticalChange,
    onUncriticalChange,
    operations,
    paymentMethodOptions,
    payments,
    restorePayment,
    runTool,
    undoToOperation,
  }
}

export type EditPaymentsDialogState = ReturnType<typeof useEditPaymentsDialogState>

export const useEditPaymentsDialog = () => {
  const { t } = useTranslation()

  return useDialog<EditPaymentsDialogProps, SignedCalendarSimulation, EditPaymentsDialogState | undefined>({
    icon: CalendarCog,
    title: t('loan.payment.edit'),
    subtitle: (ctx) =>
      ctx.state
        ? t('loan.payment.editPayments.scope', {
            count: ctx.state.scope.count,
            from: ctx.state.scope.from ?? '',
            to: ctx.state.scope.to ?? '',
            contractNumber: ctx.state.scope.contractNumber,
          })
        : null,
    maxWidth: 'lg',
    fullWidth: true,
    edgeToEdge: true,
    confirmLabel: t('loan.payment.editPayments.confirmChanges'),
    useDialogState: useEditPaymentsDialogState,
    toolbar: (ctx) => {
      const state = ctx.state
      if (!state) return null
      return (
        <Stack spacing={1}>
          {state.error && <ErrorAlert error={state.error} disabledGutterBottom />}
          {!state.isLockedByOther && (
            <EditPaymentsToolbar
              onTool={(tool, batchUpdate) => void state.runTool(tool, ctx.close, batchUpdate)}
              onSmsReminderBatchChange={state.handleSmsReminderBatchChange}
              canSpreadOverdueAmount={state.canSpreadOverPayment}
              hasAmountNotReplaced={state.hasAmountNotReplaced}
            />
          )}
        </Stack>
      )
    },

    additionalActions: (ctx) =>
      ctx.state ? (
        <EditPaymentsImpact
          simulationBefore={ctx.state.initialSignedSimulation.simulation}
          simulationAfter={ctx.state.currentSimulation.simulation}
        />
      ) : null,

    confirmAdjacentActions: (ctx) =>
      ctx.state ? (
        <EditPaymentsReviewChanges
          operations={ctx.state.operations}
          onUndoTo={ctx.state.undoToOperation}
          loading={ctx.state.isRecomputing}
          disabled={ctx.state.isLockedByOther}
        />
      ) : null,

    confirmLoading: (ctx) => Boolean(ctx.state?.isRecomputing),

    confirmDisabled: (ctx) => !ctx.state || ctx.state.isLockedByOther || !ctx.state.hasEdits || !ctx.state.isValid,

    confirmTooltip: (ctx) => {
      if (!ctx.state) return null
      if (ctx.state.isLockedByOther) return t('common.lockTooltip.lockedByOther')
      if (ctx.state.isRecomputing) return t('loan.payment.editPayments.recomputing')
      if (!ctx.state.hasEdits) return t('loan.payment.editPayments.nothingToConfirm')
      if (!ctx.state.isValid) return t('loan.payment.editPayments.invalidRows')
      return null
    },

    content: (ctx) => (ctx.state ? <EditPaymentsDialogBody state={ctx.state} closeEditor={ctx.close} /> : null),

    onConfirm: async (ctx) => {
      if (!ctx.state) return false
      const confirmed = await ctx.state.getConfirmedSimulation()
      return confirmed ?? false
    },
  })
}
